import { Course } from '@/types/courseTypes';

export const githubAIDevelopmentCourse: Course = {
  id: 'github-ai-development',
  title: 'GitHub Copilot & AI-Assisted Development',
  description: 'Master AI-powered coding with GitHub Copilot, automate workflows, and boost developer productivity',
  level: 'beginner',
  duration: '14 hours',
  durationHours: 14,
  image: '/placeholder.svg',
  tags: ['GitHub Copilot', 'AI Coding', 'Developer Tools', 'GitHub Actions', 'Productivity'],
  prerequisites: ['Basic programming', 'Git fundamentals', 'IDE usage'],
  learningOutcomes: [
    'Write code 40% faster with GitHub Copilot',
    'Use Copilot Chat for code explanations and debugging',
    'Automate ML workflows with GitHub Actions',
    'Build custom GitHub Apps with AI integration',
    'Implement best practices for AI-assisted development'
  ],
  type: 'specialized-ai',
  modules: [
    {
      id: 'github-module-1',
      title: 'GitHub Copilot Fundamentals',
      description: 'Get started with AI pair programming',
      content: `# GitHub Copilot Fundamentals

## What is GitHub Copilot?

GitHub Copilot is an AI pair programmer that suggests code and entire functions in real-time.

### Key Features
- Code completion and generation
- Multi-language support (Python, JavaScript, TypeScript, Go, Ruby, etc.)
- Context-aware suggestions
- Documentation generation
- Test creation

## Setup & Configuration

### Installation
\`\`\`bash
# VS Code: Install GitHub Copilot extension
# JetBrains: Install from marketplace
# Neovim: Use copilot.vim

# Authenticate
:Copilot setup  # In VS Code/Neovim
\`\`\`

### Configuration
\`\`\`json
// settings.json
{
  "github.copilot.enable": {
    "*": true,
    "yaml": false,
    "plaintext": false
  },
  "github.copilot.editor.enableAutoCompletions": true
}
\`\`\`

## Prompt Engineering for Code

### Effective Comments
\`\`\`python
# Function to calculate fibonacci sequence up to n terms using memoization
def fibonacci(n):
    # Copilot generates optimized implementation
    
# Create a REST API endpoint that handles user authentication with JWT
@app.route('/api/auth/login', methods=['POST'])
def login():
    # Copilot generates complete auth logic
\`\`\`

### Context-Driven Suggestions
\`\`\`javascript
// Copilot understands context from imports and existing code
import { useState, useEffect } from 'react';
import axios from 'axios';

function UserProfile({ userId }) {
  // Type this and Copilot suggests complete implementation:
  const [user, setUser] = useState(null);
}
\`\`\`

## Keyboard Shortcuts

### Essential Shortcuts
- **Accept suggestion**: Tab
- **Reject suggestion**: Esc
- **Next suggestion**: Alt + ]
- **Previous suggestion**: Alt + [
- **Open Copilot**: Ctrl + Enter
- **Inline chat**: Ctrl + I

## Best Practices

### Do's
✓ Write clear, descriptive comments
✓ Provide context through variable names
✓ Review and test generated code
✓ Use for boilerplate and repetitive tasks

### Don'ts
✗ Blindly accept all suggestions
✗ Skip security reviews
✗ Ignore code quality
✗ Forget to add tests`,
      duration: '3 hours',
      quiz: {
        questions: [
          {
            id: 'gh1-q1',
            question: 'What is the primary benefit of GitHub Copilot?',
            options: [
              'Replaces the need for developers',
              'Accelerates coding with AI-powered suggestions',
              'Automatically deploys applications',
              'Manages Git repositories'
            ],
            correctAnswer: 1,
            explanation: 'Copilot acts as an AI pair programmer, suggesting code completions and functions to accelerate development.'
          }
        ]
      }
    },
    {
      id: 'github-module-2',
      title: 'Advanced Copilot Techniques',
      description: 'Master multi-file context, test generation, and refactoring',
      content: `# Advanced Copilot Techniques

## Multi-File Context

Copilot analyzes related files in your workspace for better suggestions.

### Workspace Awareness
\`\`\`javascript
// models/User.js
export class User {
  constructor(name, email) { ... }
}

// controllers/UserController.js
import { User } from '../models/User';

class UserController {
  // Copilot knows about User model structure
  async createUser(req, res) {
    // Generates code using correct User constructor
  }
}
\`\`\`

## Test Generation

### Unit Tests
\`\`\`python
def calculate_total_price(items, tax_rate):
    subtotal = sum(item['price'] for item in items)
    return subtotal * (1 + tax_rate)

# Generate unit tests for the above function
def test_calculate_total_price():
    # Copilot suggests comprehensive test cases:
    items = [{'price': 10}, {'price': 20}]
    assert calculate_total_price(items, 0.1) == 33.0
    assert calculate_total_price([], 0.1) == 0.0
    assert calculate_total_price(items, 0) == 30.0
\`\`\`

### Integration Tests
\`\`\`javascript
// Copilot generates API integration tests
describe('User API', () => {
  it('should create a new user', async () => {
    const response = await request(app)
      .post('/api/users')
      .send({ name: 'John', email: 'john@example.com' });
    
    expect(response.status).toBe(201);
    expect(response.body).toHaveProperty('id');
  });
});
\`\`\`

## Code Refactoring

### Pattern Recognition
\`\`\`python
# Before: Repetitive code
def get_user_name(user_id):
    user = db.query(User).filter(User.id == user_id).first()
    return user.name if user else None

def get_user_email(user_id):
    user = db.query(User).filter(User.id == user_id).first()
    return user.email if user else None

# Copilot suggests refactoring:
def get_user_attribute(user_id, attribute):
    user = db.query(User).filter(User.id == user_id).first()
    return getattr(user, attribute, None) if user else None
\`\`\`

## Documentation Generation

\`\`\`typescript
/**
 * Copilot generates JSDoc from function signature
 */
async function fetchUserData(userId: string, includeOrders: boolean = false) {
  // Implementation
}

// Copilot suggests:
/**
 * Fetches user data from the database
 * @param userId - The unique identifier of the user
 * @param includeOrders - Whether to include user's order history
 * @returns Promise resolving to user data object
 * @throws {NotFoundError} If user doesn't exist
 */
\`\`\``,
      duration: '4 hours',
      quiz: {
        questions: [
          {
            id: 'gh2-q1',
            question: 'How does Copilot improve test generation?',
            options: [
              'It runs tests automatically',
              'It suggests comprehensive test cases based on function logic',
              'It fixes failing tests',
              'It deploys tests to production'
            ],
            correctAnswer: 1,
            explanation: 'Copilot analyzes function logic and suggests relevant test cases, edge cases, and assertions.'
          }
        ]
      }
    },
    {
      id: 'github-module-3',
      title: 'GitHub Copilot Chat & CLI',
      description: 'Conversational coding and command-line AI assistance',
      content: `# GitHub Copilot Chat & CLI

## Copilot Chat

### Inline Chat
\`\`\`
# Press Ctrl+I in your editor
> Refactor this function to use async/await
> Add error handling to this API call
> Explain what this regex does
\`\`\`

### Chat Panel
\`\`\`
# Open chat panel (Ctrl+Alt+I)
User: How do I implement rate limiting in Express?

Copilot: Here's how to implement rate limiting...
[Generates code with express-rate-limit]
\`\`\`

### Slash Commands
- **/explain**: Explain selected code
- **/fix**: Suggest fixes for errors
- **/tests**: Generate tests
- **/doc**: Generate documentation

## GitHub CLI with AI

### Installation
\`\`\`bash
# Install GitHub CLI
brew install gh  # macOS
winget install GitHub.cli  # Windows

# Enable Copilot in CLI
gh extension install github/gh-copilot
\`\`\`

### CLI Usage
\`\`\`bash
# Get command suggestions
gh copilot suggest "list all running docker containers"
# Output: docker ps

# Explain commands
gh copilot explain "tar -xzf archive.tar.gz"
# Output: Extract files from gzip-compressed tar archive

# Generate git commands
gh copilot suggest "undo last commit but keep changes"
# Output: git reset --soft HEAD~1
\`\`\`

## Debugging with Copilot

\`\`\`python
# Select error-causing code
def process_data(items):
    total = 0
    for item in items:
        total += item.price  # AttributeError: 'dict' object has no attribute 'price'
    return total

# Ask Copilot Chat: "/fix this error"
# Copilot suggests:
def process_data(items):
    total = 0
    for item in items:
        total += item.get('price', 0)  # Safe dict access
    return total
\`\`\`

## Code Review Assistance

\`\`\`javascript
// Select code block, ask in chat:
// "Review this code for security issues"

function loginUser(username, password) {
  const query = \`SELECT * FROM users WHERE username = '\${username}' AND password = '\${password}'\`;
  // Copilot warns: SQL injection vulnerability!
  // Suggests: Use parameterized queries
}
\`\`\``,
      duration: '3 hours',
      quiz: {
        questions: [
          {
            id: 'gh3-q1',
            question: 'What does the /fix slash command do in Copilot Chat?',
            options: [
              'Fixes Git merge conflicts',
              'Suggests fixes for errors in selected code',
              'Repairs broken dependencies',
              'Formats code automatically'
            ],
            correctAnswer: 1,
            explanation: 'The /fix command analyzes selected code and suggests corrections for errors and bugs.'
          }
        ]
      }
    },
    {
      id: 'github-module-4',
      title: 'GitHub Actions for AI/ML',
      description: 'Automate ML workflows with CI/CD',
      content: `# GitHub Actions for AI/ML

## CI/CD for ML Models

### Model Training Workflow
\`\`\`yaml
# .github/workflows/train-model.yml
name: Train ML Model

on:
  push:
    paths:
      - 'models/**'
      - 'data/**'

jobs:
  train:
    runs-on: ubuntu-latest
    steps:
      - uses: actions/checkout@v3
      
      - name: Setup Python
        uses: actions/setup-python@v4
        with:
          python-version: '3.10'
      
      - name: Install dependencies
        run: pip install -r requirements.txt
      
      - name: Train model
        run: python train.py
      
      - name: Upload model artifacts
        uses: actions/upload-artifact@v3
        with:
          name: trained-model
          path: models/saved_model
\`\`\`

## Model Deployment

### Deploy to Hugging Face
\`\`\`yaml
- name: Push to Hugging Face Hub
  env:
    HF_TOKEN: \${{ secrets.HF_TOKEN }}
  run: |
    python -c "
    from huggingface_hub import HfApi
    api = HfApi()
    api.upload_folder(
      folder_path='models/saved_model',
      repo_id='username/model-name',
      token='$HF_TOKEN'
    )"
\`\`\`

## Model Versioning

\`\`\`yaml
- name: Create model release
  uses: actions/create-release@v1
  env:
    GITHUB_TOKEN: \${{ secrets.GITHUB_TOKEN }}
  with:
    tag_name: model-v\${{ github.run_number }}
    release_name: Model Release v\${{ github.run_number }}
\`\`\`

## Automated Testing

\`\`\`yaml
- name: Test model accuracy
  run: |
    python test_model.py
    python -c "
    import json
    with open('results.json') as f:
      results = json.load(f)
      assert results['accuracy'] > 0.85
    "
\`\`\``,
      duration: '4 hours',
      quiz: {
        questions: [
          {
            id: 'gh4-q1',
            question: 'What is the benefit of using GitHub Actions for ML workflows?',
            options: [
              'Faster model training',
              'Automated training, testing, and deployment',
              'Free GPU access',
              'Better model accuracy'
            ],
            correctAnswer: 1,
            explanation: 'GitHub Actions automates the entire ML workflow from training to deployment with version control.'
          }
        ]
      }
    }
  ]
};
